import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../context/ProjectContext";
import api from "../lib/api";
import KpiCard from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { nf, pf, signed, money } from "../lib/format";
import { pfColor, gainColor } from "../lib/status";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, Cell,
} from "recharts";
import {
  Clock, Activity, TrendingUp, Gauge, Timer, Building2, Loader2, FolderKanban, ArrowRight,
} from "lucide-react";

const StatusCount = ({ label, count, color, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-3 text-center" data-testid={testid}>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{count}</div>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
  </div>
);

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { select } = useProjects();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard/executive").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B192C]" /></div>;
  if (!data || data.totals.project_count === 0)
    return <EmptyState title="No projects yet" hint="Create a project to see the company-wide executive view." />;

  const t = data.totals;
  const c = t.status_counts;
  const openProject = (id) => { select(id); navigate("/"); };

  return (
    <div className="space-y-5" data-testid="executive-dashboard-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded bg-[#0B192C] flex items-center justify-center">
          <Building2 className="w-6 h-6 text-[#E8791A]" />
        </div>
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-slate-900">Executive Overview</h1>
          <p className="text-sm text-slate-500">
            Company-wide roll-up across {t.project_count} active project{t.project_count === 1 ? "" : "s"} · {t.cost_code_count} cost codes
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="exec-kpi-revised" label="Revised Budget Hours" icon={Clock} value={nf(t.total_revised_hours)} />
        <KpiCard testid="exec-kpi-actual" label="Actual Hours" icon={Activity} value={nf(t.total_actual_hours)}
                 sub={`ST ${nf(t.total_st_hours)} · OT ${nf(t.total_ot_hours)}`} />
        <KpiCard testid="exec-kpi-earned" label="Earned Hours" icon={TrendingUp} value={nf(t.total_earned_hours)} />
        <KpiCard testid="exec-kpi-pf" label="Overall Productivity Factor" icon={Gauge}
                 value={pf(t.overall_productivity_factor)} accent={pfColor(t.overall_productivity_factor)}
                 sub={t.overall_productivity_factor >= 1 ? "Favorable" : "Unfavorable"} />
        <KpiCard testid="exec-kpi-forecast" label="Forecast Hours @ Completion" icon={Timer} value={nf(t.total_forecast_hours)} />
        <KpiCard testid="exec-kpi-forecast-gain" label="Forecast Hours Gain/(Loss)" value={signed(t.total_forecast_gain_loss)}
                 accent={gainColor(t.total_forecast_gain_loss)} />
        <KpiCard testid="exec-kpi-jtd-hours" label="JTD Hours Gain/(Loss)" value={signed(t.jtd_hours_gain_loss)}
                 accent={gainColor(t.jtd_hours_gain_loss)} />
        <KpiCard testid="exec-kpi-jtd-dollars" label="JTD Dollar Gain/(Loss)" value={money(t.jtd_dollar_gain_loss)}
                 accent={gainColor(t.jtd_dollar_gain_loss)} />
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatusCount testid="exec-status-on-track" label="On Track" count={c.on_track} color="#047857" />
        <StatusCount testid="exec-status-watch" label="On Watch" count={c.watch} color="#B45309" />
        <StatusCount testid="exec-status-unfavorable" label="Unfavorable" count={c.unfavorable} color="#B91C1C" />
        <StatusCount testid="exec-status-review" label="Needs Review" count={c.review} color="#1D4ED8" />
        <StatusCount testid="exec-status-not-started" label="Not Started" count={c.not_started} color="#64748B" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Earned vs Actual Hours by Project" testid="exec-chart-earned-actual">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_project} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="earned_hours" name="Earned" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual_hours" name="Actual" fill="#0B192C" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Forecast Hours Gain/(Loss) by Project" testid="exec-chart-forecast-gain">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_project} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="forecast_gain_loss" name="Fcst Hrs +/-" radius={[3, 3, 0, 0]}>
                {data.by_project.map((e, i) => (
                  <Cell key={i} fill={e.forecast_gain_loss >= 0 ? "#10B981" : "#EF4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weekly Straight-Time vs Overtime (All Projects)" testid="exec-chart-weekly-hours">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.weekly} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week_ending" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="st" name="Straight-Time" stackId="h" fill="#1E3E62" />
              <Bar dataKey="ot" name="Overtime" stackId="h" fill="#E8791A" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weekly Productivity-Factor Trend (All Projects)" testid="exec-chart-weekly-pf">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.weekly} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="week_ending" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
              <Tooltip />
              <Line type="monotone" dataKey="pf" name="PF" stroke="#E8791A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Per-project breakdown */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <h3 className="font-head font-semibold p-3 border-b border-slate-100 flex items-center gap-2">
          <FolderKanban className="w-4 h-4 text-slate-500" /> Project Breakdown
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="exec-project-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Project</th>
                <th className="px-3 py-2 text-right">Rev Hrs</th>
                <th className="px-3 py-2 text-right">Actual Hrs</th>
                <th className="px-3 py-2 text-right">Earned Hrs</th>
                <th className="px-3 py-2 text-right">PF</th>
                <th className="px-3 py-2 text-right">Fcst Hrs +/-</th>
                <th className="px-3 py-2 text-right">JTD Hrs +/-</th>
                <th className="px-3 py-2 text-right">JTD $ +/-</th>
                <th className="px-3 py-2 text-center">Cost Codes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.per_project.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openProject(p.id)} data-testid={`exec-project-row-${p.project_number}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs text-slate-500">#{p.project_number}</div>
                    <div className="font-medium">{p.project_name}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{nf(p.revised_hours)}</td>
                  <td className="px-3 py-2 text-right font-mono">{nf(p.actual_hours)}</td>
                  <td className="px-3 py-2 text-right font-mono">{nf(p.earned_hours)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${pfColor(p.productivity_factor)}`}>{pf(p.productivity_factor)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${gainColor(p.forecast_gain_loss)}`}>{signed(p.forecast_gain_loss)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${gainColor(p.jtd_hours_gain_loss)}`}>{signed(p.jtd_hours_gain_loss)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${gainColor(p.jtd_dollar_gain_loss)}`}>{money(p.jtd_dollar_gain_loss)}</td>
                  <td className="px-3 py-2 text-center font-mono">{p.cost_code_count}</td>
                  <td className="px-3 py-2 text-right"><ArrowRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top movers across all projects */}
      <div className="grid lg:grid-cols-2 gap-4">
        <TopList title="Top 5 Favorable Cost Codes (All Projects)" rows={data.top_favorable} positive testid="exec-top-favorable" />
        <TopList title="Top 5 Unfavorable Cost Codes (All Projects)" rows={data.top_unfavorable} testid="exec-top-unfavorable" />
      </div>
    </div>
  );
}

const ChartCard = ({ title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm" data-testid={testid}>
    <h3 className="font-head text-base font-semibold text-slate-800 mb-3">{title}</h3>
    {children}
  </div>
);

const TopList = ({ title, rows, positive, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm" data-testid={testid}>
    <h3 className="font-head text-base font-semibold text-slate-800 mb-3">{title}</h3>
    {(!rows || rows.length === 0) ? (
      <div className="text-sm text-slate-400 py-4 text-center">No data</div>
    ) : (
      <div className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between py-2 text-sm">
            <div className="min-w-0">
              <span className="font-mono font-medium">{r.cost_code}</span>
              <span className="text-slate-500 ml-2">{r.description}</span>
              <span className="text-[11px] text-slate-400 ml-2">#{r.project_number}</span>
            </div>
            <span className={`font-mono font-semibold shrink-0 ${positive ? "text-emerald-700" : "text-red-700"}`}>
              {signed(r.jtd_hours_gain_loss)} hrs
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
